import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  STATUS_FAMILY_PILL,
  type StatusFamily,
} from "@/lib/ui/status-families";

/**
 * Faixa de aviso (README §3: "banner de alerta/IA: fundo chapado semântico,
 * sem gradiente"). A cor é a da família pelo SIGNIFICADO (azul = novidade ou
 * espera de outra pessoa, laranja = prazo, vermelho = bloqueio/atraso);
 * texto à esquerda, ações à direita — no celular elas descem para baixo do
 * texto. Uma faixa só por assunto; nada de banner fixo de dica.
 *
 * `detail`: o que a faixa abre embaixo do texto quando a pessoa pede (o "Ver
 * detalhes" do aviso de uso do editor de modelos — entrega 9g4); quem decide
 * se aparece é a tela, pela ação.
 */
export default function Banner({
  family = "blue",
  icon: Icon,
  title,
  description,
  actions,
  detail,
  descriptionClassName,
  className,
}: {
  family?: StatusFamily;
  icon?: LucideIcon;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Botões/links da faixa (no máximo 2). */
  actions?: React.ReactNode;
  /** Conteúdo a mais embaixo do texto (uma lista, por exemplo). */
  detail?: React.ReactNode;
  /** Ex.: "hidden lg:block" — no celular, só o título e a ação. */
  descriptionClassName?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-xl px-4 py-3 sm:flex-row sm:items-center sm:gap-4",
        STATUS_FAMILY_PILL[family],
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {Icon && <Icon aria-hidden className="mt-0.5 size-4.5 shrink-0" />}
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-sm font-semibold">{title}</p>
          {description && (
            <p className={cn("text-ui", descriptionClassName)}>{description}</p>
          )}
          {detail && <div className="mt-1 text-ui">{detail}</div>}
        </div>
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 pl-7.5 sm:pl-0">
          {actions}
        </div>
      )}
    </div>
  );
}
